import { Col, Form, Row } from "react-bootstrap";
import { FormEvent, useState, useEffect } from 'react'
import { UserFormDto } from "../types";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom'
import "react-datepicker/dist/react-datepicker.css";
import { getUserById, createUser, updateUser, getSelectorsData } from "../api/usersAPI";
import AlertMessage from "../../../../components/AlertMessage";
import { AlertType } from "../../../../hooks/useAlert";
import { getUserFormSchema } from "../schema/userSchema";
import AppMultiSelect from "../../../../components/AppInput/AppMultiSelect";
import { SelectItemDto, TreeSelectItemDto } from "../../../../types/SelectItemDto";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useQueryClient } from "@tanstack/react-query";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";
import AppHierarchicalMultiSelect from "../../../../components/AppHierarchicalMultiSelect/AppHierarchicalMultiSelect";

export default function UserForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { idUser } = useParams<{ idUser: string }>();
    const isEditMode = Boolean(idUser);
    const [roleOptions, setRoleOptions] = useState<SelectItemDto[]>([]);
    const [organizationalStructureOptions, setOrganizationalStructureOptions] = useState<TreeSelectItemDto[]>([]);

    const [ userForm, setUserForm ] = useState<UserFormDto>({
        id: 0,
        email: '',
        name: '',
        language: 'CA',
        roles: [],
        organizationalStructures: []
    })

    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const [loading, setLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState<AlertType>('success'); // Puede ser 'success' o 'danger'

    useEffect(() => {
        if (idUser) {
            setLoading(true);
            getUserById(idUser)
                .then(data => setUserForm(data))
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                })
                .finally(() => setLoading(false));            
        }
    }, [idUser]);

    useEffect(() => {
        getSelectorsData()
            .then((selectorData) => {
                setRoleOptions(selectorData.roles);
                setOrganizationalStructureOptions(selectorData.organizationalStructures);
            })
            .catch((err: ApiErrorResponse) => {
                console.error(err);
            });
    }, []);

    const updateField = <FormField extends keyof UserFormDto>(field: FormField, value: UserFormDto[FormField]) => {
        setUserForm(prev => ({
            ...prev,
            [field]: value
        }));
        setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        setLoading(true);
        const schema = getUserFormSchema(t);
        setErrors({});

        try {
            await schema.validate(userForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            setLoading(false);
            return;
        }

        try {
            let message;
            if (isEditMode && idUser) {
                await updateUser(idUser, userForm);
                setAlertType('success');
                message = t("user:savedSuccessfully");
            } else {
                await createUser(userForm);
                message =  t("user:modal.messageCreateUserSuccessfully");
                
            }
            navigate('/admin/users', { state: { message: message } });
            queryClient.invalidateQueries({
                queryKey: ['getUsers'],
                exact: false,
                refetchType: 'all',
            });
        } catch (e: any) {
            setLoading(false);
            setAlertType('danger');

            let errorMessage = e?.response?.data?.message || e?.message || "Ha ocurrido un error inesperado.";
            const apiErrors = e?.response?.data?.errors;

            if (Array.isArray(apiErrors)) {
                const parsedErrors: { [key: string]: string | undefined } = {};
                apiErrors.forEach((error: any) => {
                    if (error?.field) {
                        parsedErrors[error.field] = error?.message ? t(error.message) : t("common:error");
                    }
                });
                if (Object.keys(parsedErrors).length > 0) {
                    setErrors(parsedErrors);
                }
            } else if (apiErrors && typeof apiErrors === "object") {
                const parsedErrors: { [key: string]: string | undefined } = {};
                Object.entries(apiErrors).forEach(([field, message]) => {
                    const value = Array.isArray(message) ? message[0] : message;
                    parsedErrors[field] = value ? t(String(value)) : t("common:error");
                });
                if (Object.keys(parsedErrors).length > 0) {
                    setErrors(parsedErrors);
                }
            }

            if (errorMessage === "ERROR_DUPLICATE_USER") {
                errorMessage = t("user:errors.newUserDuplicate");
                setErrors(prev => ({ ...prev, email: errorMessage }));
            }
            if (errorMessage === "ERROR_EMAIL_ALREADY_EXISTS") {
                errorMessage = t("user:errors.newUserExistingEmail");
                setErrors(prev => ({ ...prev, email: errorMessage }));
            }

            setAlertMessage(errorMessage);
            setShowAlert(true);
        }
    };

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>{isEditMode ? t("user:buttonEditUser") : t("user:buttonNewUser")}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={() => handleSubmit()}
                        onCancelClick={() => navigate('/admin/users')}
                        cancelLabel={isEditMode ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")}
                        saveLabel={loading ? t("common:buttons.buttonSaveingProgress") : t("common:buttons.buttonSave")}
                        disabled={loading}
                        showSaveButton={userForm.deletedDate == null}
                    />
                </Col>
                {showAlert && (
                    <AlertMessage
                        message={alertMessage}
                        type={alertType}
                        onClose={() => setShowAlert(false)}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <Form onSubmit={handleSubmit}>
                        {isEditMode && (
                            <Row>
                                <Col xs={4} md={4} style={{ marginBottom: "10px" }}>
                                    <Form.Group>
                                        <Form.Label>{t("user:forms.newUserInputTextId")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={idUser}
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col xs={8} md={8}>
                                <Form.Group>
                                    <Form.Label>{t("user:forms.newUserInputEmailId")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={userForm.email}
                                        isInvalid={!!errors.email}
                                        onChange={(e) => updateField("email", e.target.value)}
                                        disabled={userForm.deletedDate != null}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={4} md={4}>
                                <Form.Label>{t("user:forms.newUserInputLanguagelId")}</Form.Label>
                                <Form.Select
                                    value={userForm.language || ""}
                                    onChange={(e) => {
                                        updateField("language", e.target.value);
                                    }}
                                    disabled={userForm.deletedDate != null}

                                >
                                    {userForm.language && !["CA", "ES", "EN"].includes(userForm.language) && (
                                        <option value={userForm.language}>{userForm.language}</option>
                                    )}
                                    <option value="CA">CA</option>
                                    <option value="ES">ES</option>
                                    <option value="EN">EN</option>
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col xs={4} md={4}>
                                <Form.Group>
                                    <Form.Label>{t("user:forms.newUserInputNamelId")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={userForm.name}
                                        isInvalid={!!errors.name}
                                        onChange={(e) => updateField("name", e.target.value)}
                                        disabled={userForm.deletedDate != null}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={4} md={4}>
                                <Form.Group>
                                    <Form.Label>{t("user:forms.newUserInputSurnamelId")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={userForm.surname || ""}
                                        isInvalid={!!errors.surname}
                                        onChange={(e) => updateField("surname", e.target.value)}
                                        disabled={userForm.deletedDate != null}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.surname}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={4} md={4}>
                                <Form.Group>
                                    <Form.Label>{t("user:forms.newUserInputSurnamelId")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={userForm.surname2 || ""}
                                        isInvalid={!!errors.surname2}
                                        onChange={(e) => updateField("surname2", e.target.value)}
                                        disabled={userForm.deletedDate != null}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.surname2}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col xs={4} md={4}>
                                <Form.Label>{t("user:forms.newUserInputRole")}</Form.Label>
                                <AppMultiSelect
                                    options={roleOptions}
                                    selectedValues={(userForm.roles || [])}
                                    onChange={(values) => updateField("roles", values.map((value) => Number(value)))}
                                    isInvalid={!!errors.roles}
                                    disabled={userForm.deletedDate != null}

                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.roles}
                                </Form.Control.Feedback>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col xs={4} md={4}>
                                <Form.Label>{t("user:forms.newUserInputOrgStructure")}</Form.Label>
                                <AppHierarchicalMultiSelect
                                    options={organizationalStructureOptions}
                                    selectedValues={(userForm.organizationalStructures || [])}
                                    onChange={(values) => updateField("organizationalStructures", values.map((value) => Number(value)))}
                                    isInvalid={!!errors.organizationalStructures}
                                    disabled={userForm.deletedDate != null}

                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.organizationalStructures}
                                </Form.Control.Feedback>
                            </Col>
                        </Row>                      
                    </Form>
                </Col>
            </Row>
        </>
    );
}


